/**
 * SMS template rendering + segment (cost) estimation.
 * Shared between the worker (dispatch/preview) and the organizer UI
 * (live character counter + cost estimate while composing).
 * Placeholders: {{guest_name}} {{couple_names}} {{date}} {{venue}} {{rsvp_link}}
 *               {{date2}} {{venue2}} {{event2_name}}  ← opsiyonel ikinci etkinlik
 */

export interface SmsTemplateVars {
  guest_name: string;
  couple_names: string;
  date: string;
  venue: string;
  rsvp_link: string;
  /** Opt-out linki — dispatch'te doldurulur; preview/UI'da olmayabilir. */
  opt_out_link?: string;
  /**
   * İkinci etkinlik (kına/nikah/after-party). Düğünde ikinci etkinlik yoksa
   * çağıranlar bunları `""` ile doldurur (date/venue konvansiyonuyla aynı) →
   * placeholder ham kalmaz, boşalır.
   */
  date2?: string;
  venue2?: string;
  event2_name?: string;
}

export function renderSmsTemplate(template: string, vars: SmsTemplateVars): string {
  const map = vars as unknown as Record<string, string | undefined>;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => {
    const v = map[key];
    return v != null ? v : match; // bilinmeyen/undefined placeholder ham kalır
  });
}

// GSM-7 basic + extension charset. Anything outside forces UCS-2 encoding
// (Turkish ğ, ş, ı, İ, Ğ, Ş are NOT in GSM-7 → most Turkish texts are UCS-2).
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENSION = "^{}\\[~]|€";

/**
 * Estimate how many SMS segments a message will consume.
 * GSM-7: 160 chars single / 153 per segment when concatenated
 * (extension chars count double). UCS-2: 70 single / 67 concatenated.
 */
export function estimateSmsSegments(text: string): { encoding: "gsm7" | "ucs2"; segments: number; length: number } {
  let gsmUnits = 0;
  let isGsm = true;
  for (const ch of text) {
    if (GSM7_BASIC.includes(ch)) gsmUnits += 1;
    else if (GSM7_EXTENSION.includes(ch)) gsmUnits += 2;
    else { isGsm = false; break; }
  }

  if (isGsm) {
    const segments = gsmUnits === 0 ? 0 : gsmUnits <= 160 ? 1 : Math.ceil(gsmUnits / 153);
    return { encoding: "gsm7", segments, length: gsmUnits };
  }

  const len = [...text].length;
  const segments = len === 0 ? 0 : len <= 70 ? 1 : Math.ceil(len / 67);
  return { encoding: "ucs2", segments, length: len };
}
